import React, { useEffect, useReducer, useCallback, useRef, useState } from "react";
import { Col, Form, Row, Select } from "antd";
import { fetchAddressUnit } from "../../utils/addressHelper";
import produce from "immer";

const { Option } = Select;

const initialState = {
  aimagHotList: [],
  sumDuuregList: [],
  bagHorooList: [],
  pages: { aimagHot: 1, sumDuureg: 1, bagHoroo: 1 },
  hasMore: { aimagHot: false, sumDuureg: false, bagHoroo: false },
};

const actions = {
  resetLists: "RESET_LISTS",
  appendList: "APPEND_LIST",
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case actions.resetLists: {
      const { aimagHotList, sumDuuregList, bagHorooList } = action.payload;
      if (aimagHotList != null) {
        draft.aimagHotList = aimagHotList.docs;
        draft.pages.aimagHot = 1;
        draft.hasMore.aimagHot = aimagHotList.hasMore;
      }
      if (sumDuuregList != null) {
        draft.sumDuuregList = sumDuuregList.docs;
        draft.pages.sumDuureg = 1;
        draft.hasMore.sumDuureg = sumDuuregList.hasMore;
      }
      if (bagHorooList != null) {
        draft.bagHorooList = bagHorooList.docs;
        draft.pages.bagHoroo = 1;
        draft.hasMore.bagHoroo = bagHorooList.hasMore;
      }
      break;
    }
    case actions.appendList: {
      const { listKey, docs, page, hasMore } = action.payload;
      draft[`${listKey}List`].push(...docs);
      draft.pages[listKey] = page;
      draft.hasMore[listKey] = hasMore;
      break;
    }
    default:
      break;
  }
});

/** Хоосон жагсаалт — доод түвшнийг тодорхой цэвэрлэхэд хэрэглэнэ */
const EMPTY_LIST = { docs: [], hasMore: false };

/**
 * Татсан хариунуудыг аль түвшний жагсаалт болохоор нь эрэмбэлж, reducer-ийн
 * payload болгоно.
 *
 * Жагсаалтын түвшнийг ХҮСЭЛТИЙН ЭЦЭГ id-аас тодорхойлно (эцэг байхгүй = аймаг,
 * 2 оронтой = сум, 4 оронтой = хороо). Өмнө нь `docs[0]._id`-ийн уртаас
 * таамагладаг байсан. Гэвч сум/хорооны жагсаалт `_id`-гүй, зөвхөн `code`-той
 * ирэх тохиолдолд ямар ч түвшинд онооход хүрэхгүй, `sumDuuregList` нь `null`
 * хэвээр үлдэж, reducer нь `null`-ыг "өөрчлөлт байхгүй" гэж үзэн ХУУЧИН
 * жагсаалтыг хадгалдаг байв. Ингэснээр Архангай сонгоход өмнөх (Баянхонгорын)
 * сумууд гарсаар байж, аймгийн сонголт сумын жагсаалтад нөлөөлөхгүй байсан.
 *
 * Мөн дээд түвшин цэвэрлэгдэхэд доод түвшний хуучин жагсаалтыг ТОДОРХОЙ
 * хоослоно — эс тэгвээс өмнөх аймгийн сумууд, өмнөх сумын хороод жагсаалтад
 * үлдэж, буруу хаяг сонгох боломж гарна.
 *
 * @param {Array<{unitId: string|number|null, docs: Array, hasNextPage: boolean}>} responses
 * @param {Array} selectedUnits `[аймаг, сум, хороо]` — сонгогдсон нэгжүүд
 * @param {object} [preserved] аль хэдийн мэдэгдэж байгаа жагсаалтууд (`null` = өөрчлөхгүй)
 * @returns {{aimagHotList: object|null, sumDuuregList: object|null, bagHorooList: object|null}}
 */
export const resolveAddressLists = (responses = [], selectedUnits = [], preserved = {}) => {
  let {
    aimagHotList = null,
    sumDuuregList = null,
    bagHorooList = null,
  } = preserved;

  for (const { unitId, docs, hasNextPage } of responses) {
    const item = { docs: docs ?? [], hasMore: hasNextPage ?? false };
    const parentIdLength = unitId == null ? 0 : String(unitId).length;

    if (parentIdLength === 0) {
      aimagHotList = item;
    } else if (parentIdLength === 2) {
      sumDuuregList = item;
    } else if (parentIdLength === 4) {
      bagHorooList = item;
    }
  }

  if (!selectedUnits?.[0]) {
    sumDuuregList = sumDuuregList ?? EMPTY_LIST;
    bagHorooList = bagHorooList ?? EMPTY_LIST;
  } else if (!selectedUnits?.[1]) {
    bagHorooList = bagHorooList ?? EMPTY_LIST;
  }

  return { aimagHotList, sumDuuregList, bagHorooList };
};

/**
 * Тогтмол хоосон утга. `= []` бол render бүрт шинэ массив болж `[selectedUnits]`
 * effect дахин дахин ажилладаг — loading state-ийн render-тэй нийлээд эцэс
 * төгсгөлгүй давталтад ордог.
 */
const NO_SELECTED_UNITS = [];

const AddressUnitsSelector = ({
  selectedUnits = NO_SELECTED_UNITS,
  selectedCustomerField,
  onChange,
  isErlian,
  form: formProp,
  /**
   * Юу ч сонгоогүй үед ЭХНИЙ аймаг/сум/хороог автоматаар сонгох эсэх.
   *
   * Захиалгын урсгалуудад анхдагч хаяг тохиромжтой байдаг тул үндсэн утга нь
   * `true` — одоо байгаа бүх дуудлагын зан төлөв хэвээр. Харин салбар бүртгэх
   * зэрэг хаяг нь ЗААВАЛ санаатай сонголт байх ёстой формуудад `false` өгнө:
   * эс тэгвээс хэрэглэгч огт хүрээгүй атал талбарууд чимээгүй бөглөгдөж,
   * заавал шалгуур давж, буруу хаягтай бүртгэл үүснэ.
   */
  autoSelectFirst = true,
  styleProps = {
    rowGutter: [16, 0],
    columnSpans: [24, 24, 24],
  },
}) => {
  /**
   * `form` prop-ыг дамжуулдаггүй дуудлагууд бий (`clients/Add.js`,
   * `request/CitizenForm.js`, `request/OrganizationForm.js`,
   * `request/Steps/Step2.js`). Тэр үед доорх бүх `form?.setFieldsValue` чимээгүй
   * алгасагдаж, хот солиход дүүрэг/хороо ХАРАГДАХ талбар дээрээ цэвэрлэгддэггүй
   * байв — өмнөх аймагт харьяалагдахгүй утгатай харилцагч хадгалагдах эрсдэлтэй.
   * Тиймээс эцэг `<Form>`-ын instance-ыг context-оос нөхөж авна.
   */
  const contextForm = Form.useFormInstance();
  const form = formProp ?? contextForm;

  const [state, dispatch] = useReducer(reducer, initialState, undefined);
  const isFetching = useRef({ aimagHot: false, sumDuureg: false, bagHoroo: false });
  // Хаягийн нэгжүүдийг татаж байх үед Select-үүд дээр spinner харуулна
  const [loadingUnits, setLoadingUnits] = useState(false);

  useEffect(() => {
    if (isErlian)
      form.setFieldsValue({
        aimagHot: "Улаанбаатар",
        sumDuureg: "Хан-Уул дүүрэг",
        bagHoroo: "1-р хороо",
      });
  }, [isErlian, form]);

  const fetchUnitId = (unitId, index) => {
    if (unitId) {
      const strId = String(unitId);
      const types = ["aimagHot", "sumDuureg", "bagHoroo"];
      const type = types[index];
      if (type === "aimagHot") {
        return !state.aimagHotList.some((ah) => ah._id === strId) ? null : false;
      } else if (type === "sumDuureg") {
        return !state.sumDuuregList.some((sd) => sd._id === strId)
          ? strId.slice(0, 2)
          : false;
      } else if (type === "bagHoroo") {
        return !state.bagHorooList.some((bh) => bh._id === strId)
          ? strId.slice(0, 4)
          : false;
      }
      return true;
    }
    if (selectedUnits?.[index - 1]?.addressId) {
      return String(selectedUnits[index - 1].addressId);
    }
    return false;
  };

  const fetchData = async () => {
    let aimagHotList = null;
    let sumDuuregList = null;
    let bagHorooList = null;

    const [aimagHot, sumDuureg, bagHoroo] = selectedUnits.map(
      (u) => u?.addressId ?? null,
    );

    if (aimagHot) {
      const strAimag = String(aimagHot);
      const contains = state.aimagHotList.some((ah) => ah._id === strAimag);
      if (contains) {
        aimagHotList = { docs: state.aimagHotList, hasMore: state.hasMore.aimagHot };
      }
    }
    if (sumDuureg) {
      const strSum = String(sumDuureg);
      const contains = state.sumDuuregList.some((sd) => sd._id === strSum);
      if (contains) {
        sumDuuregList = { docs: state.sumDuuregList, hasMore: state.hasMore.sumDuureg };
      }
    }
    if (bagHoroo) {
      const strBag = String(bagHoroo);
      const contains = state.bagHorooList.some((bh) => bh._id === strBag);
      if (contains) {
        bagHorooList = { docs: state.bagHorooList, hasMore: state.hasMore.bagHoroo };
      }
    }

    let unitIdsToFetch = selectedUnits
      .map((u) => u?.addressId ?? null)
      .map(fetchUnitId)
      .filter((unitId) => unitId !== false);

    if (!unitIdsToFetch.length && !selectedUnits?.[0]) {
      // `autoSelectFirst: false` үед зөвхөн аймгийн жагсаалтыг татна — сонголтыг
      // хэрэглэгч өөрөө хийнэ (`isErlian`-тай ижил зам)
      if (!isErlian && autoSelectFirst) {
        const aimagResult = await fetchAddressUnit(null);
        const defAimag = aimagResult.docs[0];
        if (!defAimag) return;

        const duuregResult = await fetchAddressUnit(defAimag._id);
        const defDuureg = duuregResult.docs[0];
        if (!defDuureg) return;

        const horooResult = await fetchAddressUnit(defDuureg._id);
        const defHoroo = horooResult.docs[0];
        if (!defHoroo) return;

        dispatch({
          type: actions.resetLists,
          payload: {
            aimagHotList: { docs: aimagResult.docs, hasMore: aimagResult.hasNextPage },
            sumDuuregList: { docs: duuregResult.docs, hasMore: duuregResult.hasNextPage },
            bagHorooList: { docs: horooResult.docs, hasMore: horooResult.hasNextPage },
          },
        });

        form?.setFieldsValue({
          aimagHot: defAimag.name,
          sumDuureg: defDuureg.name,
          bagHoroo: defHoroo.name,
        });
        onChange?.([
          { order: 100, type: "aimaghot", value: defAimag.name, addressId: defAimag._id, name: defAimag.name },
          { order: 200, type: "sumduureg", value: defDuureg.name, addressId: defDuureg._id, name: defDuureg.name },
          { order: 300, type: "baghoroo", value: defHoroo.name, addressId: defHoroo._id, name: defHoroo.name },
        ]);
        return;
      }
      unitIdsToFetch = [null];
    }

    const unitsListResponses = await Promise.all(
      unitIdsToFetch.map(async (unitId) => ({ unitId, ...(await fetchAddressUnit(unitId)) })),
    );

    dispatch({
      type: actions.resetLists,
      payload: resolveAddressLists(unitsListResponses, selectedUnits, {
        aimagHotList,
        sumDuuregList,
        bagHorooList,
      }),
    });
  };

  useEffect(() => {
    setLoadingUnits(true);
    fetchData().finally(() => setLoadingUnits(false));
    // fetchData нь state.*List-ээс уншиж, dispatch-аар мөн түүнийг өөрчилдөг тул
    // deps-д оруулбал давталтад орно. Зөвхөн selectedUnits солигдоход дуудна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnits]);

  const handleChange = async (type, value, option) => {
    let updatedValue = [];

    switch (type) {
      case "aimagHot": {
        form?.setFieldsValue({ sumDuureg: "", bagHoroo: "" });
        updatedValue = [option, null, null];
        break;
      }
      case "sumDuureg": {
        form?.setFieldsValue({ bagHoroo: "" });
        const cloned = [...selectedUnits];
        cloned[1] = option;
        cloned[2] = null;
        updatedValue = cloned;
        break;
      }
      case "bagHoroo": {
        const cloned = [...selectedUnits];
        cloned[2] = option;
        updatedValue = cloned;
        break;
      }
      default:
        return;
    }
    onChange(updatedValue);
  };

  const handlePopupScroll = useCallback(
    async (listKey, unitId, e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop <= clientHeight + 20 &&
        state.hasMore[listKey] &&
        !isFetching.current[listKey]
      ) {
        isFetching.current[listKey] = true;
        const nextPage = state.pages[listKey] + 1;
        const { docs, hasNextPage } = await fetchAddressUnit(unitId, nextPage);
        dispatch({
          type: actions.appendList,
          payload: { listKey, docs, page: nextPage, hasMore: hasNextPage },
        });
        isFetching.current[listKey] = false;
      }
    },
    [state.hasMore, state.pages],
  );

  const getNotFoundContent = (unit) => {
    let messageText = "Хот/Аймаг сонгоно уу";
    if (unit === "sumDuureg") {
      if (!selectedUnits?.[0]) {
        messageText = "Аймаг/хот сонгоно уу";
      }
    }
    if (unit === "bagHoroo") {
      if (!selectedUnits?.[1]) {
        messageText = "Сум/дүүрэг сонгоно уу";
      }
    }
    return (
      <div style={{ textAlign: "center", color: "#a2a2a2" }}>{messageText}</div>
    );
  };

  return (
    <Row gutter={styleProps.rowGutter}>
      <Col xs={24} md={8} xl={6}>
        <Form.Item
          label="Хот/Аймаг"
          name="aimagHot"
          rules={[{ required: true, message: "Хот/Аймаг сонгоно уу!" }]}
        >
          <Select
            className="all_buttons"
            placeholder="Сонгох"
            value={selectedUnits?.[0]?.name}
            disabled={isErlian ? true : false}
            onChange={(value, option) => handleChange("aimagHot", value, option)}
            size="large"
            style={{ marginBottom: 5 }}
            notFoundContent={getNotFoundContent("aimagHot")}
            loading={loadingUnits}
            onPopupScroll={(e) => handlePopupScroll("aimagHot", null, e)}
          >
            {state.aimagHotList.map((unit) => {
              const props = {
                order: 100,
                type: "aimaghot",
                value: unit.name,
                addressId: unit._id,
                name: unit?.name,
              };
              return <Option key={unit.name} {...props}>{unit.name}</Option>;
            })}
          </Select>
        </Form.Item>
      </Col>

      <Col xs={24} md={8} xl={6}>
        <Form.Item
          label="Дүүрэг/Сум"
          name="sumDuureg"
          rules={[{ required: true, message: "Дүүрэг/Сум сонгоно уу!" }]}
        >
          <Select
            className="all_buttons"
            placeholder="Сонгох"
            size="large"
            value={selectedUnits?.[1]?.name}
            // Хот/аймаг сонгоогүй байхад дүүргийн жагсаалт нээгдэж болохгүй —
            // өмнөх аймгийн сумууд харагдаж, буруу хаяг сонгогдоно
            disabled={isErlian || !selectedUnits?.[0]}
            onChange={(value, option) => handleChange("sumDuureg", value, option)}
            style={{ marginBottom: 5 }}
            notFoundContent={getNotFoundContent("sumDuureg")}
            loading={loadingUnits}
            onPopupScroll={(e) =>
              handlePopupScroll("sumDuureg", selectedUnits[0]?.addressId, e)
            }
          >
            {state.sumDuuregList.map((unit) => {
              const props = {
                order: 200,
                type: "sumduureg",
                value: unit.name,
                addressId: unit._id ?? unit.code,
                name: unit.name,
              };
              return <Option key={unit.name} {...props}>{props.name}</Option>;
            })}
          </Select>
        </Form.Item>
      </Col>

      <Col xs={24} md={8} xl={6}>
        <Form.Item
          label="Хороо/Баг"
          name="bagHoroo"
          rules={[{ required: true, message: "Хороо/Баг сонгоно уу!" }]}
        >
          <Select
            className="all_buttons"
            size="large"
            placeholder="Сонгох"
            value={selectedUnits?.[2]?.name}
            disabled={isErlian || !selectedUnits?.[1]}
            onChange={(value, option) => handleChange("bagHoroo", value, option)}
            style={{ marginBottom: 5 }}
            notFoundContent={getNotFoundContent("bagHoroo")}
            loading={loadingUnits}
            onPopupScroll={(e) =>
              handlePopupScroll("bagHoroo", selectedUnits[1]?.addressId, e)
            }
          >
            {state.bagHorooList.map((unit) => {
              const props = {
                order: 300,
                type: "baghoroo",
                value: unit.name,
                addressId: unit._id ?? unit.code,
                name: unit.name,
              };
              return <Option key={unit.name} {...props}>{props.name}</Option>;
            })}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );
};

export default AddressUnitsSelector;
